/**
 * "Before you send" notes pinned to the words they are about: the
 * browser's side of the rule in api/_shared/aiTools/annotationAnchors.ts.
 *
 * A note with an anchor shows only while its words are in the email body as
 * it reads now (the textarea's current text), so deleting the words hides
 * the note and putting them back shows it again. A note with no anchor
 * always shows. Matching is exact after normalizing: every run of
 * whitespace reads as one space, and curly quotes and apostrophes read as
 * straight ones. Case is not folded. The words match whole: an anchor that
 * starts or ends with a letter or digit does not match inside a longer
 * word ("Ann" is not in "Anna"). The server's copy is pinned to the
 * same cases (tests/ui/annotationAnchors.test.ts and
 * api/_shared/__tests__/annotationAnchors.test.ts).
 */

import type { EmailAnnotation } from './api';

const SINGLE_QUOTES = /[‘’‚‛′]/;
const DOUBLE_QUOTES = /[“”„‟″]/;

function normalizeChar(ch: string): string {
  if (SINGLE_QUOTES.test(ch)) return "'";
  if (DOUBLE_QUOTES.test(ch)) return '"';
  return ch;
}

/** The text as anchors are matched: quotes straightened, whitespace runs as one space, trimmed. */
export function normalizeAnchorText(text: string): string {
  return Array.from(text, normalizeChar).join('').replace(/\s+/g, ' ').trim();
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

/**
 * Where a normalized needle first occurs in a normalized haystack as whole
 * words, or -1 (the server's wholeWordIndex, the same rule).
 */
function wholeWordIndex(hay: string, needle: string): number {
  if (needle.length === 0) return -1;
  const startsWord = WORD_CHAR.test(needle[0]!);
  const endsWord = WORD_CHAR.test(needle[needle.length - 1]!);
  for (let k = hay.indexOf(needle); k >= 0; k = hay.indexOf(needle, k + 1)) {
    const before = k > 0 ? hay[k - 1]! : '';
    const after = k + needle.length < hay.length ? hay[k + needle.length]! : '';
    if (startsWord && before !== '' && WORD_CHAR.test(before)) continue;
    if (endsWord && after !== '' && WORD_CHAR.test(after)) continue;
    return k;
  }
  return -1;
}

/**
 * Where the anchor's words first appear in the text, as a [start, end)
 * range of the text itself (not of its normalized form), or null when they
 * do not appear. The range starts at the anchor's first character and ends
 * after its last, so selecting it selects exactly the words.
 */
export function findAnchorRange(text: string, anchor: string): { start: number; end: number } | null {
  const needle = normalizeAnchorText(anchor);
  if (needle.length === 0) return null;
  // The normalized text, with each of its characters' index in the original.
  let norm = '';
  const at: number[] = [];
  let inSpace = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (/\s/.test(ch)) {
      if (!inSpace) {
        norm += ' ';
        at.push(i);
        inSpace = true;
      }
      continue;
    }
    inSpace = false;
    norm += normalizeChar(ch);
    at.push(i);
  }
  const k = wholeWordIndex(norm, needle);
  if (k < 0) return null;
  return { start: at[k]!, end: at[k + needle.length - 1]! + 1 };
}

/** The note's anchor, or null when it has none (absent, null or blank). */
export function noteAnchor(note: EmailAnnotation): string | null {
  const a = note.anchor;
  return typeof a === 'string' && a.trim().length > 0 ? a : null;
}

/** Whether a note shows against the body as it reads now. */
export function annotationShows(note: EmailAnnotation, body: string): boolean {
  const anchor = noteAnchor(note);
  return anchor === null || findAnchorRange(body, anchor) !== null;
}
